/**
 * Every word a scene draws, in the reader's language — and the organization's own wall texts where
 * it set them. Shared by the Bug Hunt and the room so the two never say different things.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import type { ActivityState } from '../lib/types'
import type { SceneLabels } from './Scene'

/**
 * What an agent is doing, in words that sit under a person's name.
 *
 * Every state reads as the agent's except one: "Idle" under somebody's name says the person is
 * idle, which §43 rules out and which is not what it means — only that the agent has not reported
 * for a while. That one gets its own wording.
 */
export function agentActivity(state: ActivityState, t: (key: string) => string): string {
  return state === 'Idle' ? t('bugHunt.agentQuiet') : t(`state.${state}`)
}

export function useSceneLabels(): SceneLabels {
  const { t } = useI18n()

  // What the organization wrote on its walls, if anything. Rarely changes.
  const { data: roomTexts } = useQuery({ queryKey: ['room-texts'], queryFn: api.room.texts, staleTime: 5 * 60_000 })

  return useMemo(
    () => ({
      containment: t('bugHunt.containment'),
      waiting: (count: number) => t('bugHunt.waitingCount', { count }),
      andMore: (count: number) => t('bugHunt.andMore', { count }),
      noAgent: t('activity.noAgent'),
      working: t('bugHunt.working'),
      allClear: t('bugHunt.allClear'),
      allClearHint: t('bugHunt.allClearHint'),
      readyForReview: t('bugHunt.readyForReviewBanner'),
      blocked: t('bugState.Blocked'),
      underTest: t('bugState.InTesting'),
      testing: t('bugHunt.testsRunning'),
      bugState: (state) => t(`bugState.${state}`),
      activity: (state) => agentActivity(state, t),
      onProject: (project: string) => t('room.onProject', { project }),
      // The organization's own words where it set them; the product's, in the reader's language,
      // for every sign it left empty.
      neon: [0, 1, 2].map((i) => roomTexts?.neon?.[i] || t(`bugHunt.room.neon${i + 1}`)) as [string, string, string],
      motto: [0, 1, 2, 3].map((i) => roomTexts?.motto?.[i] || t(`bugHunt.room.motto${i + 1}`)) as [string, string, string, string],
      routineTitle: roomTexts?.routineTitle || t('bugHunt.room.routineTitle'),
      routine: [0, 1, 2, 3].map((i) => roomTexts?.routine?.[i] || t(`bugHunt.room.routine${i + 1}`)) as [string, string, string, string],
    }),
    [t, roomTexts],
  )
}
